import { toast } from '@approvalmax/ui/src/components';
import { errorHelpers } from '@approvalmax/utils';
import { useForm } from 'react-hook-form';
import { useUpdateKyc, useUpdatePerson } from 'shared/data/walletsKyc/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';

import { WizardStep } from '../../../resources/constants';
import { useWizardNavigation } from '../hooks/useWizardNavigation';
import { messages } from '../messages';
import type { FormValues } from '../types';
import { useStepSubmission } from './useStepSubmission';
import { useWizardActiveStep } from './useWizardActiveStep';
import { useWizardFormInitialValues } from './useWizardFormInitialValues';
import { useWizardFormResolver } from './useWizardFormResolver';

export const useWizardForm = () => {
    const { companyId } = useCompanyIdBasedOnUrl();
    const { navigateNext } = useWizardNavigation();
    const activeWizardStep = useWizardActiveStep();

    const { mutateAsync: updateKyc } = useUpdateKyc({
        onSuccess: () => {
            navigateNext();
        },
    });
    const { mutateAsync: updatePerson } = useUpdatePerson();

    const form = useForm<FormValues>({
        // TODO set `any` type because this will be fixed
        //  incrementally via many tasks later
        ...(useWizardFormInitialValues() as any),
        ...useWizardFormResolver(),
    });

    const { onStepSubmit } = useStepSubmission();

    const handleSubmit = form.handleSubmit(async (values) => {
        if (activeWizardStep === WizardStep.PersonsSettings && values.people?.persons) {
            return Promise.all(
                values.people.persons.map((person) => {
                    return updatePerson({
                        params: { path: { organizationId: companyId } },
                        body: {
                            person: {
                                documentIds: [],
                                ...person,
                            },
                        },
                    });
                })
            ).then(() => {
                navigateNext();
            });
        }

        let data;

        try {
            data = await onStepSubmit(values);
        } catch (error) {
            errorHelpers.captureException(error);
            toast.error(messages.documentsUploadFailed);

            return;
        }

        return updateKyc({
            params: { path: { organizationId: companyId } },
            // TODO: right now BE expects `accountUsage.documentIds` which we don't even send
            //  I'm trying to negotiate removing this field from the schema
            body: { data } as any,
        });
    });

    return { form, handleSubmit };
};
